import { datadogRum } from "@datadog/browser-rum";
import { Checkbox } from "@mantine/core";
import { useAtom, useAtomValue } from "jotai";
import { useMutation } from "react-query";
import { useNavigate } from "react-router-dom";

import { useStyles } from "pages/Welcome/styles";

import Button from "components/Button";
import Flex from "components/Flex";
import { Image } from "components/Image";
import PageLayout from "components/PageLayout";
import Text from "components/Text";

import { startSession } from "api/startSession";
import { DigitalJourneyRoute } from "app/DigitalJourneyAppFlow/constants";
import { clientConfigAtom } from "atoms/clientConfig";
import { clientIdAtom } from "atoms/clientId";
import { sessionIdAtom } from "atoms/sessionId";
import { tosAcceptedAtom } from "atoms/tosAccepted";
import { useClientTranslation } from "hooks/useClientTranslation";
import { useDigitalJourneyPageRouting } from "hooks/useDigitalJourneyPageRouting";
import { useEventTracking } from "hooks/useEventTracking";
import { shouldRetry } from "utils/networkRequestHelper";

const DEFAULT_IMAGE: Image = {
  src: "/assets/svg/welcome.svg",
};

export const DJWelcomePage = () => {
  const {
    welcome: { illustrations = {} },
  } = useAtomValue(clientConfigAtom);
  const [tosAccepted, setTosAccepted] = useAtom(tosAcceptedAtom);
  const sessionId = useAtomValue(sessionIdAtom);
  const clientId = useAtomValue(clientIdAtom);
  const { trackEvent } = useEventTracking();
  const { onNext } = useDigitalJourneyPageRouting();
  const { t } = useClientTranslation();
  const navigate = useNavigate();

  const image = {
    ...DEFAULT_IMAGE,
    ...(illustrations?.main ?? {}),
  };
  const { classes, theme } = useStyles({ image });

  const { isLoading, mutate } = useMutation(() => startSession(sessionId, clientId), {
    retry: shouldRetry,
    onSuccess: () => {
      trackEvent({ name: "Digital Journey Welcome - Start Session" });
      if (tosAccepted) onNext();
    },
    onError: (error) => {
      datadogRum.addError(error, {
        message: "Digital Journey - Failed to start session",
      });

      navigate(DigitalJourneyRoute.ApiError);
    },
  });

  return (
    <PageLayout showNavbar contentSpacing="space-between">
      <div className={classes.illustration} />
      <Flex px={24} py={24} gap={16} flex={1} mb={16}>
        <Text sizeVariant="sm" weight={400}>
          {t("digital_journey.welcome.hello")}
        </Text>
        <Text dataTestId="djWelcomeHeader" as="h1" sizeVariant="xl" weight={600}>
          {t("digital_journey.welcome.title")}
        </Text>
        <Text mb={16}>
          {t("digital_journey.welcome.first_disclaimer")}{" "}
          <a
            href={t("digital_journey.welcome.first_disclaimer_link_path")}
            style={{ color: theme.other.primary[60] }}
            target="_blank"
            rel="noreferrer"
          >
            {t("digital_journey.welcome.first_disclaimer_link_text")}
          </a>
          .
        </Text>
        <Text>
          <Text as="span" weight={700}>
            {t("digital_journey.welcome.second_disclaimer_note")}{" "}
          </Text>
          {t("digital_journey.welcome.second_disclaimer")}
        </Text>
      </Flex>
      <Flex px={24} py={24} gap={16}>
        <Flex gap={8} direction="row">
          <Checkbox
            size="sm"
            radius="sm"
            sx={{
              ".mantine-Checkbox-input:checked": {
                backgroundColor: theme.other.primary[60],
                borderColor: theme.other.primary[60],
              },
            }}
            style={{ color: theme.other.primary[60] }}
            checked={tosAccepted}
            onChange={(event) => setTosAccepted(event.currentTarget.checked)}
          />
          <Text>
            {t("digital_journey.welcome.privacy_policy_text")}
            <a
              href={t("digital_journey.welcome.privacy_policy_link_path")}
              style={{ color: theme.other.primary[60] }}
              target="_blank"
              rel="noreferrer"
            >
              {t("digital_journey.welcome.privacy_policy_link_text")}
            </a>
          </Text>
        </Flex>
        <Button loading={isLoading} disabled={!tosAccepted || isLoading} onClick={mutate}>
          {t("global.continue")}
        </Button>
      </Flex>
    </PageLayout>
  );
};
